/**
 * Enhanced base64 download utility with robust error handling
 * Handles various base64 formats and provides user-friendly error messages
 */

/**
 * Extract MIME type from base64 data URL prefix
 * @param {string} base64String - Raw base64 string that may contain MIME info
 * @returns {string|null} - Extracted MIME type or null
 */
const extractMimeTypeFromDataUrl = (base64String) => {
  if (!base64String) return null;

  // Pattern 1: Standard data URL - "data:application/pdf;base64,"
  const standardMatch = base64String.match(/^data:([^;,]+)/);
  if (standardMatch) {
    return standardMatch[1];
  }

  // Pattern 2: Compressed format - "dataapplication/pdfbase64"
  // Match common MIME types without separators
  const compressedPatterns = [
    { regex: /^data(application\/pdf)base64/i, mime: 'application/pdf' },
    { regex: /^data(application\/vnd\.openxmlformats-officedocument\.wordprocessingml\.document)base64/i, mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' },
    { regex: /^data(application\/vnd\.openxmlformats-officedocument\.spreadsheetml\.sheet)base64/i, mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
    { regex: /^data(application\/msword)base64/i, mime: 'application/msword' },
    { regex: /^data(application\/vnd\.ms-excel)base64/i, mime: 'application/vnd.ms-excel' },
    { regex: /^data(image\/png)base64/i, mime: 'image/png' },
    { regex: /^data(image\/jpeg)base64/i, mime: 'image/jpeg' },
    { regex: /^data(image\/jpg)base64/i, mime: 'image/jpeg' },
    { regex: /^data(image\/gif)base64/i, mime: 'image/gif' },
    { regex: /^data(image\/webp)base64/i, mime: 'image/webp' },
    { regex: /^data(text\/plain)base64/i, mime: 'text/plain' },
    { regex: /^data(text\/csv)base64/i, mime: 'text/csv' },
  ];

  for (const pattern of compressedPatterns) {
    if (pattern.regex.test(base64String)) {
      return pattern.mime;
    }
  }

  return null;
};

/**
 * Detect MIME type from base64 content (magic numbers)
 * @param {string} base64String - Cleaned base64 string
 * @returns {string|null} - Detected MIME type or null
 */
const detectMimeTypeFromContent = (base64String) => {
  if (!base64String || base64String.length < 10) return null;

  try {
    // Decode first few bytes to check magic numbers
    const firstChars = base64String.substring(0, 20);
    const decoded = atob(firstChars);

    // PDF: starts with "%PDF"
    if (decoded.startsWith('%PDF')) {
      return 'application/pdf';
    }

    // PNG: starts with "\x89PNG"
    if (decoded.charCodeAt(0) === 0x89 && decoded.substring(1, 4) === 'PNG') {
      return 'image/png';
    }

    // JPEG: starts with "\xFF\xD8\xFF"
    if (decoded.charCodeAt(0) === 0xFF && decoded.charCodeAt(1) === 0xD8 && decoded.charCodeAt(2) === 0xFF) {
      return 'image/jpeg';
    }

    // DOCX/XLSX (ZIP format): starts with "PK"
    if (decoded.substring(0, 2) === 'PK') {
      // Both DOCX and XLSX are ZIP files, default to DOCX
      // Will be overridden by file extension if provided
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    }

    // GIF: starts with "GIF87a" or "GIF89a"
    if (decoded.substring(0, 3) === 'GIF') {
      return 'image/gif';
    }
  } catch (error) {
    // If decoding fails, return null
    return null;
  }

  return null;
};

/**
 * Clean and validate base64 string
 * @param {string} base64String - Raw base64 string from API
 * @returns {object} - { cleanedData: string, detectedMime: string|null }
 * @throws {Error} - If base64 format is invalid
 */
const cleanBase64String = (base64String) => {
  if (!base64String || typeof base64String !== 'string') {
    throw new Error('Data file tidak tersedia');
  }

  let cleanData = base64String;
  let detectedMime = null;

  // Extract MIME type from data URL if present
  detectedMime = extractMimeTypeFromDataUrl(base64String);

  // Step 1: Remove data URL scheme if present (e.g., "data:application/pdf;base64,...")
  if (cleanData.includes('data:') && cleanData.includes(',')) {
    const commaIndex = cleanData.indexOf(',');
    if (commaIndex !== -1) {
      cleanData = cleanData.substring(commaIndex + 1);
    }
  }
  // Step 2: Handle compressed format without separators (e.g., "dataapplication/pdfbase64JVBERi...")
  else if (cleanData.startsWith('data') && cleanData.includes('base64')) {
    // Find "base64" keyword position
    const base64Index = cleanData.indexOf('base64');
    if (base64Index !== -1) {
      // Skip past "base64" (6 characters) and any following comma
      cleanData = cleanData.substring(base64Index + 6);
      if (cleanData.startsWith(',')) {
        cleanData = cleanData.substring(1);
      }
    }
  }
  // Step 3: Alternative format with colon but no comma (e.g., "data:application/pdfbase64...")
  else if (cleanData.includes('base64')) {
    const base64Index = cleanData.indexOf('base64');
    if (base64Index !== -1) {
      cleanData = cleanData.substring(base64Index + 6);
      if (cleanData.startsWith(',')) {
        cleanData = cleanData.substring(1);
      }
    }
  }

  // Step 4: Remove all whitespace characters (spaces, newlines, tabs, etc.)
  cleanData = cleanData.replace(/\s/g, '');

  // Step 5: Validate base64 format
  const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
  if (!base64Regex.test(cleanData)) {
    throw new Error('Format file tidak sesuai');
  }

  if (cleanData.length === 0) {
    throw new Error('File kosong atau tidak tersedia');
  }

  // Step 6: Detect MIME type from content if not already detected
  if (!detectedMime) {
    detectedMime = detectMimeTypeFromContent(cleanData);
  }

  return { cleanedData: cleanData, detectedMime };
};

/**
 * Get MIME type mapping for file extensions
 */
const getMimeTypeMapping = () => ({
  pdf: "application/pdf",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  gif: "image/gif",
  bmp: "image/bmp",
  webp: "image/webp",
  doc: "application/msword",
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  xls: "application/vnd.ms-excel",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  ppt: "application/vnd.ms-powerpoint",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  txt: "text/plain",
  csv: "text/csv",
  zip: "application/zip",
  rar: "application/x-rar-compressed",
});

/**
 * Download file from base64 string with robust error handling
 * @param {string} base64Data - Base64 encoded file data
 * @param {string} fileName - Desired file name with extension
 * @param {object} options - Optional configuration
 * @param {string} options.mimeType - Override MIME type detection
 * @param {string} options.fileExtension - File extension (optional, extracted from fileName if not provided)
 * @returns {object} - Success status and message
 */
const downloadBase64 = (base64Data, fileName, options = {}) => {
  try {
    // Validate inputs
    if (!fileName || typeof fileName !== 'string') {
      throw new Error('Nama file tidak valid');
    }

    // Clean and validate base64 string with MIME detection
    const { cleanedData: cleanBase64, detectedMime } = cleanBase64String(base64Data);

    // Decode base64 to binary
    let byteCharacters;
    try {
      byteCharacters = atob(cleanBase64);
    } catch (error) {
      throw new Error('File tidak dapat dibaca. Mohon coba lagi');
    }

    // Convert to byte array
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);

    // Determine MIME type with priority: options.mimeType > detectedMime > extension mapping > default
    const fileExtension = (
      options.fileExtension ||
      fileName.split('.').pop() ||
      'pdf'
    ).toLowerCase();

    const mimeTypes = getMimeTypeMapping();
    const mimeType = options.mimeType || detectedMime || mimeTypes[fileExtension] || "application/octet-stream";

    // Create blob
    const blob = new Blob([byteArray], { type: mimeType });

    // Validate blob size
    if (blob.size === 0) {
      throw new Error('File kosong atau tidak dapat diproses');
    }

    // Create download link
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.href = url;
    link.download = fileName;
    link.target = "_blank";
    link.rel = "noopener noreferrer";

    // Trigger download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up object URL
    setTimeout(() => URL.revokeObjectURL(url), 100);

    return {
      success: true,
      message: 'File berhasil diunduh',
      fileName,
      size: blob.size,
      mimeType, // Include detected MIME type in response
    };

  } catch (error) {
    console.error('Download error:', error);

    // User-friendly error messages
    const errorMessages = {
      'Data file tidak tersedia': 'File yang diminta tidak tersedia',
      'Format file tidak sesuai': 'Format file tidak didukung. Mohon hubungi admin',
      'File kosong atau tidak tersedia': 'File kosong. Mohon upload ulang dokumen',
      'Nama file tidak valid': 'Terjadi kesalahan pada nama file',
      'File tidak dapat dibaca. Mohon coba lagi': 'Terjadi gangguan saat membaca file. Silakan coba lagi',
      'File kosong atau tidak dapat diproses': 'File tidak dapat diproses. Mohon coba lagi',
    };

    const userMessage = errorMessages[error.message] || 'Terjadi kendala saat mengunduh file. Silakan coba beberapa saat lagi';

    return {
      success: false,
      message: userMessage,
      technicalError: error.message,
      fileName,
    };
  }
};

/**
 * Enhanced download from API response with automatic extraction
 * @param {object} apiResponse - API response object
 * @param {object} apiResponse.data - Response data containing file info
 * @param {string} apiResponse.data.file - Base64 file string
 * @param {string} apiResponse.data.ext - File extension
 * @param {string} apiResponse.data.fileName - File name
 * @param {string} apiResponse.data.mimeType - MIME type
 * @param {string} fallbackFileName - Fallback file name if not in response
 * @returns {object} - Download result
 */
export const downloadFromApiResponse = (apiResponse, fallbackFileName = 'dokumen') => {
  try {
    if (!apiResponse || !apiResponse.data || !apiResponse.data.data) {
      throw new Error('Data file tidak tersedia');
    }

    const { file, ext, fileName, mimeType } = apiResponse.data.data;

    if (!file) {
      throw new Error('File kosong atau tidak tersedia');
    }

    const fullFileName = fileName
      ? `${fileName}.${ext || 'pdf'}`
      : `${fallbackFileName}.${ext || 'pdf'}`;

    return downloadBase64(file, fullFileName, {
      fileExtension: ext,
      mimeType,
    });

  } catch (error) {
    console.error('API download error:', error);
    return {
      success: false,
      message: error.message === 'Data file tidak tersedia'
        ? 'File yang diminta tidak tersedia'
        : 'Terjadi kendala saat mengunduh file. Silakan coba beberapa saat lagi',
      technicalError: error.message,
    };
  }
};

export default downloadBase64;
